/**
 * TogglePill - Selectable pill button
 *
 * Core component: No internal dependencies.
 * Used for preset selection, multi-select chips, etc.
 *
 * Selected: blue background
 * Unselected: white background with border
 */

import { cn } from '@/lib/utils';

export interface TogglePillProps {
  /** Display label */
  label: string;
  /** Whether the pill is selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional className */
  className?: string;
}

export function TogglePill({
  label,
  isSelected,
  onClick,
  disabled = false,
  size = 'md',
  className,
}: TogglePillProps) {
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-5 py-3 text-base',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-full font-medium',
        'transition-all duration-200',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'border',
        'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1',
        sizeStyles[size],
        isSelected
          ? 'bg-sky-500 text-white border-sky-500'
          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 active:bg-slate-100',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {label}
    </button>
  );
}
